import React from 'react'

import { CloseIcon } from '@toasttab/buffet-pui-icons'

import {
  AvailableFilters,
  CatalogCustomAttributeType
} from 'src/apollo/onlineOrdering'
import PriceFromRange from 'shared/components/common/price/PriceFromRange'
import { useFormatPrice } from 'src/lib/js/hooks/useFormatPrice'
import { t } from 'src/translations'

import { getCountryDisplayName, matchByIdOrName } from './filterUtils'
import { useFilterToggle } from './useFilterToggle'

export const ActiveFilterChips = ({
  availableFilters
}: {
  availableFilters?: AvailableFilters | null
}) => {
  const {
    selectedTags,
    selectedProductOptions,
    selectedCustomAttributes,
    selectedPriceRange,
    selectedBrands,
    selectedCountriesOfOrigin,
    inStockOnly,
    handleTagToggle,
    handleProductOptionToggle,
    handleCustomAttributeToggle,
    handleInStockOnlyToggle,
    handleBrandToggle,
    handleCountryToggle,
    clearPriceRange,
    clearAll
  } = useFilterToggle()

  const minPriceFormatted = useFormatPrice(selectedPriceRange?.min ?? 0)
  const maxPriceFormatted = useFormatPrice(selectedPriceRange?.max ?? 0)

  type Chip = {
    key: string
    label: React.ReactNode
    ariaLabel?: string
    onRemove: () => void
  }

  const tagChips: Chip[] = selectedTags.map((tag) => ({
    key: `tag-${tag.guid}`,
    label: tag.name,
    onRemove: () => handleTagToggle(tag.guid, tag.name)
  }))

  const productOptionChips: Chip[] = selectedProductOptions.flatMap((option) =>
    option.values.map((value) => ({
      key: `option-${option.name}-${value}`,
      label: value,
      onRemove: () => handleProductOptionToggle(option.name, value)
    }))
  )

  const customAttributeChips: Chip[] = selectedCustomAttributes.flatMap(
    (attribute) => {
      const catalogAttr = availableFilters?.customAttributes.find(
        (catalogAttribute) => matchByIdOrName(catalogAttribute, attribute)
      )
      const isBoolean = catalogAttr?.type === CatalogCustomAttributeType.Boolean
      return attribute.values.map((value) => ({
        key: `custom-attribute-${attribute.id ?? attribute.name}-${
          value.id ?? value.name
        }`,
        label: isBoolean
          ? attribute.name ?? value.name ?? value.id ?? ''
          : value.name ?? value.id ?? '',
        onRemove: () =>
          handleCustomAttributeToggle(
            { id: attribute.id, name: attribute.name ?? '' },
            value
          )
      }))
    }
  )

  const priceChip: Chip | null = selectedPriceRange
    ? {
        key: 'price-range',
        label: (
          <PriceFromRange
            price={null}
            prices={[selectedPriceRange.min, selectedPriceRange.max]}
          />
        ),
        ariaLabel: t('filters.remove-filter', {
          filter: t('price.range-aria-label', {
            min: minPriceFormatted,
            max: maxPriceFormatted
          })
        }),
        onRemove: clearPriceRange
      }
    : null

  const inStockChip: Chip | null = inStockOnly
    ? {
        key: 'in-stock',
        label: t('filters.in-stock'),
        onRemove: handleInStockOnlyToggle
      }
    : null

  const brandChips: Chip[] = selectedBrands.map((brand) => ({
    key: `brand-${brand.id ?? brand.name}`,
    label: brand.name ?? brand.id ?? '',
    onRemove: () =>
      handleBrandToggle({
        id: brand.id ?? undefined,
        name: brand.name ?? undefined
      })
  }))

  const countryChips: Chip[] = selectedCountriesOfOrigin.map((code) => ({
    key: `country-${code}`,
    label: getCountryDisplayName(code),
    onRemove: () => handleCountryToggle(code)
  }))

  const chips: Chip[] = [
    priceChip,
    inStockChip,
    ...tagChips,
    ...productOptionChips,
    ...customAttributeChips,
    ...brandChips,
    ...countryChips
  ].filter((chip) => chip !== null)

  if (chips.length === 0) return null

  return (
    <div className='activeFilterChips'>
      <div className='chipList'>
        {chips.map((chip) => (
          <button
            key={chip.key}
            className='chip'
            onClick={chip.onRemove}
            aria-label={
              chip.ariaLabel ??
              t('filters.remove-filter', { filter: chip.label })
            }
          >
            <span className='chipLabel'>{chip.label}</span>
            <CloseIcon size='xs' aria-hidden='true' />
          </button>
        ))}
      </div>
      <button className='clearAllButton' onClick={clearAll}>
        {t('filters.clear-all')}
      </button>
    </div>
  )
}
